import { Alert, Card, CardContent, Chip, Divider, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  Show, TopToolbar, useNotify, useRecordContext, useRefresh, type RaRecord
} from 'react-admin';
import { BackLink } from './back';
import { OrderTrend } from './orderTrend';
import { Link } from 'react-router-dom';
import { callAction } from './dataProvider';

type User = RaRecord & {
  email: string; userName?: string; fullName?: string | null;
  groupId?: number | null; groupName?: string | null; points?: number;
  enabled?: boolean; locked?: boolean; verified?: boolean;
  organizationId?: number | null; organizationName?: string | null;
  orderCount?: number; roles?: string[];
  orders?: { id: number; ordered: string | null; fulfilled: boolean; organizationName: string | null }[];
  auditTrail?: {
    id: number; action: string; actorEmail: string | null; created: string;
    detail: { fromName?: string | null; toName?: string | null } | null;
  }[];
  monthly?: { month: string; orders: number }[];
  averageDeliveryDays?: number | null;
  deliveryMeasuredOn?: number;
};

interface Group { id: number; name: string; originalDiscountPercent: number; renewalDiscountPercent: number }

/**
 * Changing a group changes every price this person sees, so it is a deliberate
 * control with its own confirmation rather than a field in a form that saves
 * on blur.
 */
function GroupAssignment() {
  const record = useRecordContext<User>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [groups, setGroups] = useState<Group[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    callAction('groups', 'GET' as never)
      .then(value => setGroups((value as { data: Group[] }).data))
      .catch(() => notify('Skupiny sa nepodarilo načítať.', { type: 'warning' }));
  }, []);

  if (record == null) return null;

  const assign = async (groupId: number | null) => {
    setSaving(true);
    try {
      await callAction(`users/${record.id}/group`, 'PUT', { groupId });
      notify('Skupina zmenená. Zmenili sa tým všetky ceny, ktoré tento používateľ vidí.', { type: 'success' });
      refresh();
    } catch {
      notify('Skupinu sa nepodarilo zmeniť.', { type: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">Skupina</Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Select
            size="small"
            disabled={saving}
            value={record.groupId ?? 0}
            onChange={event => {
              const value = Number(event.target.value);
              void assign(value === 0 ? null : value);
            }}
            sx={{ minWidth: 260 }}
          >
            <MenuItem value={0}>Bez skupiny — platí štandardnú cenu</MenuItem>
            {groups.map(group => (
              <MenuItem key={group.id} value={group.id}>
                {group.name} (o: {group.originalDiscountPercent}, r: {group.renewalDiscountPercent})
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary">
            Zľavy platia len na náplne; tlačiarne sa nezľavňujú.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

/**
 * How this customer came to be on their price level.
 *
 * The group decides every price they see, so "why is this company on Díler 3"
 * is a question that gets asked months later. An empty trail is the answer for
 * most accounts: nobody has moved them since registration, so the level is
 * whatever they signed up with - which is why nothing was backfilled.
 */
function GroupHistory() {
  const record = useRecordContext<User>();
  const trail = (record?.auditTrail ?? []).filter(entry => entry.action === 'group_changed');

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">História cenovej skupiny</Typography>

        {trail.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Skupinu od registrácie nikto nemenil — platí tá, s ktorou bol účet vytvorený.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
            {trail.map(entry => (
              <Stack
                key={entry.id}
                direction="row"
                justifyContent="space-between"
                spacing={2}
                sx={{ py: 0.75 }}
              >
                <Typography variant="body2">
                  {entry.detail?.fromName ?? 'bez skupiny'} → <strong>{entry.detail?.toName ?? 'bez skupiny'}</strong>
                </Typography>
                <Stack alignItems="flex-end">
                  <Typography variant="caption" color="text.secondary">
                    {new Date(entry.created).toLocaleString('sk-SK', {
                      day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {entry.actorEmail ?? 'neznámy administrátor'}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function UserBody() {
  const record = useRecordContext<User>();
  if (record == null) return null;

  const orders = record.orders ?? [];

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6">{record.fullName ?? record.email}</Typography>
        {record.locked === true && <Chip size="small" color="error" label="Zablokovaný" />}
        {record.enabled === false && <Chip size="small" label="Neaktívny" />}
        {record.verified === false && <Chip size="small" color="warning" variant="outlined" label="Neoverený" />}
        {(record.roles ?? []).map(role => <Chip key={role} size="small" color="primary" variant="outlined" label={role} />)}
      </Stack>

      <Stack direction="row" spacing={3}>
        <Typography variant="body2">{record.email}</Typography>
        <Typography variant="body2" color="text.secondary">{record.points ?? 0} bodov</Typography>
        {record.organizationName != null && (
          <Typography variant="body2">
            <Link to={`/organizations/${record.organizationId}/show`}>{record.organizationName}</Link>
          </Typography>
        )}
      </Stack>

      <GroupAssignment />
      <GroupHistory />

      {/* The same chart the organisation gets, scoped to this person's own
          orders - a company's chart belongs on the company's page, and mixing
          them would make one buyer look like the whole account. */}
      <OrderTrend
        monthly={record.monthly ?? []}
        averageDeliveryDays={record.averageDeliveryDays ?? null}
        measuredOn={record.deliveryMeasuredOn ?? 0}
      />

      <Card variant="outlined">
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Orders {record.orderCount != null && `(${record.orderCount})`}
          </Typography>
          {orders.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Žiadne objednávky.</Typography>
          ) : (
            <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
              {orders.map(order => (
                <Stack key={order.id} direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
                  <Link to={`/orders/${order.id}/show`}>#{order.id}</Link>
                  <Typography variant="body2" color="text.secondary">
                    {order.ordered?.slice(0, 10) ?? '—'}
                    {order.organizationName != null && ` · ${order.organizationName}`}
                  </Typography>
                  {order.fulfilled
                    ? <Chip size="small" color="success" variant="outlined" label="Vybavená" />
                    : <Chip size="small" color="warning" variant="outlined" label="Otvorená" />}
                </Stack>
              ))}
            </Stack>
          )}
          {record.orderCount != null && record.orderCount > orders.length && (
            <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
              Showing the {orders.length} most recent of {record.orderCount}.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

export function UserShow() {
  return <Show title="Používateľ" actions={<TopToolbar sx={{ justifyContent: 'flex-start' }}><BackLink /></TopToolbar>}><UserBody /></Show>;
}
